import axios from 'axios';
import { clearMobileAuth, getMobileAccessToken, isNativeApp } from './mobileAuth';
import { resolveApiBaseUrl } from './apiEnvironment';
import { openAuthenticatedWebSocket } from './websocket';

// ==================== BASE URL ====================

const API = resolveApiBaseUrl({ nativeApp: isNativeApp() });

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  // Tokens are stored in httpOnly cookies — the browser sends them automatically.
  withCredentials: true,
});

const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
export const OPPONENT_REQUEST_STATUSES = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  CANCELED: 'canceled',
});

const normalizeOpponentRequestStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'cancelled') return OPPONENT_REQUEST_STATUSES.CANCELED;
  if (Object.values(OPPONENT_REQUEST_STATUSES).includes(normalized)) return normalized;
  throw new Error(`Invalid opponent request status: ${status}`);
};

const readCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const pair = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!pair) {
    return null;
  }
  return decodeURIComponent(pair.slice(name.length + 1));
};

// ==================== Out-of-Scope Endpoints ====================
// CMPAAA-V10 Out-of-Scope Endpoints (contract compliance)
function outOfScopeV10(name) {
  console.warn(`[outOfScopeV10] Attempted to call out-of-scope endpoint: ${name}`);
  return Promise.reject(new Error(`Endpoint out of scope: ${name}`));
}

// Required contract flags (must be declared, but not executed at bootstrap).
export const OUT_OF_SCOPE_V10_ENDPOINTS = Object.freeze([
  'notifications.telegram.link',
  'calendar.auth_url',
  'ai_coach.analysis',
  'leaderboard.get',
]);

// ==================== INTERCEPTORS ====================

const buildClientRequestId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // ignore and fallback below
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

api.interceptors.request.use(async (config) => {
  const next = { ...config };
  next.headers = next.headers || {};
  if (typeof FormData !== 'undefined' && next.data instanceof FormData) {
    delete next.headers['Content-Type'];
    delete next.headers['content-type'];
  }
  next.headers['X-Request-ID'] = buildClientRequestId();
  const nativeApp = isNativeApp();
  if (nativeApp) {
    next.withCredentials = false;
  }
  const mobileAccessToken = await getMobileAccessToken();
  if (mobileAccessToken && !next.headers.Authorization && !next.headers.authorization) {
    next.headers.Authorization = `Bearer ${mobileAccessToken}`;
  }
  const method = String(next.method || 'get').toLowerCase();
  if (!nativeApp && MUTATING_METHODS.has(method)) {
    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      next.headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }
  if (typeof window !== 'undefined') {
    next.headers['X-Client-Route'] = window.location.pathname || '/';
  }
  return next;
});

// Обработка ошибок авторизации
api.interceptors.response.use(
  (response) => {
    const requestId = response.headers?.['x-request-id'];
    if (requestId) {
      response.requestId = requestId;
    }
    return response;
  },
  async (error) => {
    // Handle network errors separately
    if (!error.response) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        console.error('Backend server is unavailable. Please ensure /api is reachable on the current origin.');
      } else {
        console.error('Network error:', error.message);
      }
      return Promise.reject(error);
    }

    const requestId = error.response?.headers?.['x-request-id'];
    if (error.response.status >= 500) {
      console.error('Server error', {
        requestId,
        url: error.config?.url,
        method: error.config?.method,
      });
    }

    if (error.response.status === 401) {
      if (isNativeApp()) {
        await clearMobileAuth();
        return Promise.reject(error);
      }
      // Cookie-based auth: the backend already manages token expiry.
      // Only redirect to /login if we are not already on /login and not calling /auth/me
      // (AuthContext calls /auth/me on every mount; redirecting there causes an infinite loop).
      const requestUrl = error.config?.url || '';
      const isAuthCheck = requestUrl.includes('/auth/me');
      const isAlreadyOnLogin = window.location.pathname === '/login';
      if (!isAuthCheck && !isAlreadyOnLogin) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
  verifyEmailCode: (email, code) => api.post('/auth/verify-email-code', { email, code }),
  resendVerificationEmail: (email) => api.post('/auth/resend-verification', { email }),
  getMe: () => api.get('/auth/me'),
  getFeatures: () => api.get('/auth/me/features'),
  getCsrfToken: () => api.get('/auth/csrf'),
  getWsToken: () => api.post('/auth/ws-token'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  // Logout clears the httpOnly cookie on the server side
  logout: async () => {
    try {
      return await api.post('/auth/logout');
    } finally {
      await clearMobileAuth();
    }
  },
};

// ==================== 2FA ====================

export const twoFactor = {
  enable: () => api.post('/auth/2fa/enable'),
  confirm: (token) => api.post('/auth/2fa/confirm', { token }),
  disable: (token) => api.post('/auth/2fa/disable', { token }),
  getStatus: () => api.get('/auth/2fa/status'),
  getBackupCodes: () => api.post('/auth/2fa/backup-codes'),
};

// ==================== ATHLETES ====================

export const athletes = {
  getProfile: () => api.get('/athletes/profile'),
  updateProfile: (data) => api.put('/athletes/profile', data),
  getAll: (params) => api.get('/athletes', { params }),
};

// ==================== ATHLETE DEVELOPMENT ====================

export const athleteDevelopment = {
  getProfile: () => api.get('/athlete-development/profile'),
  getRecommendations: () => api.get('/athlete-development/recommendations'),
  generateRecommendations: () => api.post('/athlete-development/recommendations/generate'),
};

// ==================== TRAININGS ====================

export const trainings = {
  create: (data) => api.post('/trainings', data),
  getAll: (params) => api.get('/trainings', { params }),
  getById: (id) => api.get(`/trainings/${id}`),
  update: (id, data) => api.put(`/trainings/${id}`, data),
  delete: (id) => api.delete(`/trainings/${id}`),
};

// ==================== COACH FEEDBACK (anonymous reviews) ====================
// Privacy contract: coach-side endpoints never include athlete_id / athlete_name.
// Athlete-peer-side endpoints (/coaches/{id}/feedback) DO include names so
// peers can write replies. See backend `app/routers/feedback.py`.

export const feedback = {
  // Athlete-side
  submit: (trainingId, data) =>
    api.post(`/feedback/training/${trainingId}`, data),
  edit: (id, data) => api.patch(`/feedback/${id}`, data),
  remove: (id) => api.delete(`/feedback/${id}`),
  listComments: (id) => api.get(`/feedback/${id}/comments`),
  addComment: (id, data) => api.post(`/feedback/${id}/comments`, data),
  // Athlete-peer view of a coach
  peerFeed: (coachId, params) =>
    api.get(`/coaches/${coachId}/feedback`, { params }),
  // Coach self-view (anonymous)
  mySummary: () => api.get('/coaches/me/feedback/summary'),
  myFeed: (params) => api.get('/coaches/me/feedback', { params }),
};


// ==================== STATISTICS ====================

export const statistics = {
  create: (data) => api.post('/statistics', data),
  getAll: (params) => api.get('/statistics', { params }),
  getAnalytics: (period) => api.get('/statistics/analytics', { params: { period } }),
};

// ==================== SCHEDULE ====================

export const schedule = {
  create: (data) => api.post('/schedule', data),
  getAll: (params) => api.get('/schedule', { params }),
  delete: (eventId) => api.delete(`/schedule/${eventId}`),
  update: (id, data) => api.put(`/schedule/${id}`, data),
};

// ==================== NUTRITION ====================

export const nutrition = {
  create: (data) => api.post('/nutrition', data),
  getAll: (params) => api.get('/nutrition', { params }),
  getEnergyExpendituresRecent: (params) => api.get('/nutrition/energy-expenditures/recent', { params }),
  getSummary: () => api.get('/nutrition/summary'),
  delete: (planId) => api.delete(`/nutrition/${planId}`),
  update: (id, data) => api.put(`/nutrition/${id}`, data),
};

// ==================== EQUIPMENT ====================

export const equipment = {
  create: (data) => api.post('/equipment', data),
  getAll: (params) => api.get('/equipment', { params }),
  getUsage: (id) => api.get(`/equipment/${id}/usage`),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
};

// ==================== WEARABLES ====================

export const wearables = {
  getConnections: () => api.get('/wearables/connections'),
  createConnection: (data) => api.post('/wearables/connections', data),
  updateConnection: (connectionId, data) => api.patch(`/wearables/connections/${connectionId}`, data),
  disconnectConnection: (connectionId) => api.post(`/wearables/connections/${connectionId}/disconnect`),
  ingestSamples: (payload) => api.post('/wearables/samples', payload),
  ingestWorkouts: (payload) => api.post('/wearables/workouts', payload),
  getWorkouts: (params) => api.get('/wearables/workouts', { params }),
  getSamplesSummary: (params) => api.get('/wearables/samples/summary', { params }),
  getDailyRecovery: (params) => api.get('/wearables/recovery/daily', { params }),
  getDailyLoad: (params) => api.get('/wearables/load/daily', { params }),
};

// ==================== TOURNAMENTS ====================

export const tournaments = {
  create: (data) => api.post('/tournaments', data),
  getAll: (params) => api.get('/tournaments', { params }),
  register: (tournamentId) => api.post(`/tournaments/${tournamentId}/register`),
  getRegistrations: () => api.get('/tournaments/registrations'),
  getById: (id) => api.get(`/tournaments/${id}`),
};

// ==================== COURTS ====================

export const courts = {
  create: (data) => api.post('/courts', data),
  getAll: (params) => api.get('/courts', { params }),
  getBookings: (params) => api.get('/courts/bookings', { params }),
  getSchedule: (courtId, date) => api.get(`/courts/${courtId}/schedule`, { params: { date } }),
  cancelBooking: (courtId, bookingId) => api.delete(`/courts/${courtId}/book/${bookingId}`),
  getMultiSchedule: (courtIds, date) => api.get('/courts/multi-schedule', { params: { court_ids: courtIds, date } }),
  getRecommendedSlots: (date) => api.get('/courts/recommended-slots', { params: { date } }),
};

// ==================== PAYMENTS ====================

export const payments = {
  createIntent: (data) => api.post('/payments/create-intent', data),
  getConfig: () => api.get('/payments/config'),
};

// ==================== MATCHMAKING ====================

export const matchmaking = {
  find: (data) => api.post('/matchmaking/find', data),
  sendRequest: (data) => api.post('/matchmaking/request', data),
  accept: (requestId) => api.post(`/matchmaking/accept/${requestId}`),
  getRequests: () => api.get('/matchmaking/requests'),
};

// ==================== OPPONENTS ====================

export const opponents = {
  search: (params) => api.get('/opponents/search', { params }),
  createRequest: (data) => api.post('/opponents/request', data),
  getRequests: () => api.get('/opponents/requests'),
  updateRequestStatus: (requestId, status) =>
    api.put(`/opponents/request/${requestId}`, null, { params: { status: normalizeOpponentRequestStatus(status) } }),
  getH2H: async (opponentId) => {
    try {
      return await api.get(`/matchmaking/history/${opponentId}`);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        // CMPAAA-201 sunset path:
        // Remove alias fallback after v1.0 + 14 days once /opponents/h2h is retired.
        return api.get(`/opponents/h2h/${opponentId}`);
      }
      throw error;
    }
  },
};

// ==================== CHAT ====================

export const chat = {
  sendMessage: async (data) => {
    try {
      return await api.post('/chat', data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        return api.post('/opponents/chat', data);
      }
      throw error;
    }
  },
  getMessages: async (requestId) => {
    try {
      return await api.get(`/chat/${requestId}`);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        return api.get(`/opponents/chat/${requestId}`);
      }
      throw error;
    }
  },
};

// ==================== ACHIEVEMENTS ====================

export const achievements = {
  getAll: (params) => api.get('/achievements', { params }),
  getStats: () => api.get('/achievements/stats'),
};

// ==================== NOTIFICATIONS ====================

export const notifications = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  sendTest: () => api.post('/notifications/test'),
  push: {
    getVapidKey: () => api.get('/notifications/push/vapid-public-key'),
    subscribe: (subscription) => api.post('/notifications/push/subscribe', subscription),
    unsubscribe: (endpoint) => api.post('/notifications/push/unsubscribe', { endpoint }),
  },
  telegram: {
    status: () => api.get('/telegram/status'),
    getAuthUrl: (fallback = false) => api.get('/telegram/auth-url', { params: { fallback } }),
    link: (chatId) => api.post('/telegram/link', { chat_id: chatId }),
    unlink: () => api.post('/telegram/unlink'),
  },
};

// ==================== PROGRESS ====================

export const progress = {
  getAnalytics: (period = '30d') => api.get('/progress/analytics', { params: { period } }),
  exportCSV: (params) => api.get('/export/statistics.csv', { params, responseType: 'blob' }),
};

// ==================== GOALS ====================

export const goals = {
  create: (data) => api.post('/goals', data),
  getAll: (params) => api.get('/goals', { params }),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
};

// ==================== MATCHES ====================

export const matches = {
  create: (data) => api.post('/matches', data),
  getAll: (params) => api.get('/matches', { params }),
  getStats: () => api.get('/matches/stats'),
  delete: (id) => api.delete(`/matches/${id}`),
  update: (id, data) => api.put(`/matches/${id}`, data),
};

// ==================== FITNESS ====================

export const fitness = {
  createActivity: (data) => api.post('/fitness/activity', data),
  getActivities: (params) => api.get('/fitness/activity', { params }),
  logRecovery: (data) => api.post('/fitness/recovery', data),
  getRecoveryLogs: (params) => api.get('/fitness/recovery', { params }),
  getRecoveryStats: () => api.get('/fitness/recovery/stats'),
  logInjury: (data) => api.post('/fitness/injury', data),
  getInjuries: (params) => api.get('/fitness/injury', { params }),
  updateInjury: (id, data) => api.put(`/fitness/injury/${id}`, data),
};

// ==================== CONNECTED TRAINING ====================

export const connectedTraining = {
  getDevices: () => api.get('/connected-devices'),
  createDevice: (data) => api.post('/connected-devices', data),
  getRecentFitnessMetrics: (params) => api.get('/fitness-metrics/recent', { params }),
  createFitnessMetric: (data) => api.post('/fitness-metrics', data),
  getRecentEquipmentWorkouts: (params) => api.get('/equipment-workouts/recent', { params }),
  createEquipmentWorkout: (data) => api.post('/equipment-workouts', data),
  createSelfLoggedEquipmentWorkout: (data) => api.post('/equipment-workouts/self-logged', data),
  importData: (data) => api.post('/connected-training/import', data),
  getMobileSourceStatuses: () => api.get('/connected-training/mobile-source-status'),
  reportMobileSourceStatus: (data) => api.post('/connected-training/mobile-source-status/report', data),
  getRecentEquipmentCapabilities: (params) => api.get('/connected-training/equipment-capabilities/recent', { params }),
  getRecentEquipmentWorkoutDeliveries: (params) => api.get('/connected-training/equipment-workout-deliveries/recent', { params }),
  getRecentPhoneGuidedEquipmentWorkouts: (params) => api.get('/connected-training/phone-guided-equipment-workouts/recent', { params }),
  createPhoneGuidedEquipmentWorkout: (data) => api.post('/connected-training/phone-guided-equipment-workouts', data),
  updatePhoneGuidedEquipmentWorkout: (id, data) => api.patch(`/connected-training/phone-guided-equipment-workouts/${id}`, data),
  completePhoneGuidedEquipmentWorkout: (id, data) => api.post(`/connected-training/phone-guided-equipment-workouts/${id}/complete`, data),
  getRecentSyncedWorkoutMatches: (params) => api.get('/connected-training/synced-workout-matches/recent', { params }),
  confirmSyncedWorkoutMatch: (id) => api.post(`/connected-training/synced-workout-matches/${id}/confirm`),
  rejectSyncedWorkoutMatch: (id) => api.post(`/connected-training/synced-workout-matches/${id}/reject`),
  importAppleHealthExport: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const deviceId = options.device_id || options.deviceId;
    return api.post('/connected-training/import/apple-health-export', formData, {
      params: deviceId ? { device_id: deviceId } : undefined,
    });
  },
  getRecentImports: (params) => api.get('/connected-training/imports/recent', { params }),
  startEquipmentWorkoutSession: (data) => api.post('/equipment-workout-sessions/start', data),
  getCurrentEquipmentWorkoutSessions: () => api.get('/equipment-workout-sessions/current'),
  getEquipmentWorkoutSession: (id) => api.get(`/equipment-workout-sessions/${id}`),
  updateEquipmentWorkoutTelemetry: (id, data) => api.patch(`/equipment-workout-sessions/${id}/telemetry`, data),
  completeEquipmentWorkoutSession: (id, data) => api.post(`/equipment-workout-sessions/${id}/complete`, data),
  cancelEquipmentWorkoutSession: (id) => api.post(`/equipment-workout-sessions/${id}/cancel`),
  getWorkoutPrescriptions: (params) => api.get('/workout-prescriptions', { params }),
  createWorkoutPrescription: (data) => api.post('/workout-prescriptions', data),
  getWorkoutPrescription: (id) => api.get(`/workout-prescriptions/${id}`),
  linkWorkoutPrescriptionToSchedule: (id, scheduleEventId) =>
    api.post(`/workout-prescriptions/${id}/link-schedule`, { schedule_event_id: scheduleEventId }),
  createWorkoutExecution: (data) => api.post('/workout-executions', data),
  getRecentWorkoutExecutions: (params) => api.get('/workout-executions/recent', { params }),
  getWorkoutExecution: (id) => api.get(`/workout-executions/${id}`),
  updateWorkoutExecution: (id, data) => api.patch(`/workout-executions/${id}`, data),
  completeWorkoutExecution: (id, data) => api.post(`/workout-executions/${id}/complete`, data),
};

// ==================== VIDEOS ====================

export const videos = {
  create: (data) => api.post('/videos', data),
  getAll: (params) => api.get('/videos', { params }),
  createAnnotation: (videoId, data) => api.post(`/videos/${videoId}/annotations`, data),
  getAnnotations: (videoId) => api.get(`/videos/${videoId}/annotations`),
  delete: (id) => api.delete(`/videos/${id}`),
};

// ==================== VIDEO ASSETS ====================

export const videoAssets = {
  createUploadSession: (payload) => api.post('/video-assets/upload-sessions', payload),
  completeUploadSession: (sessionId, payload = {}) =>
    api.post(`/video-assets/upload-sessions/${sessionId}/complete`, payload),
  getAll: (params) => api.get('/video-assets', { params }),
  getById: (videoId) => api.get(`/video-assets/${videoId}`),
  createAnalysisJob: (videoId, payload = {}) => api.post(`/video-assets/${videoId}/analysis-jobs`, payload),
  getAnalysisJobs: (videoId) => api.get(`/video-assets/${videoId}/analysis-jobs`),
  getAnalysisJob: (videoId, jobId) => api.get(`/video-assets/${videoId}/analysis-jobs/${jobId}`),
  getAnalysisResults: (videoId, params) => api.get(`/video-assets/${videoId}/analysis-results`, { params }),
  getAnalysisResult: (videoId, resultId) => api.get(`/video-assets/${videoId}/analysis-results/${resultId}`),
  getTechniqueFindings: (videoId, params) => api.get(`/video-assets/${videoId}/technique-findings`, { params }),
};

// ==================== FEED ====================

export const feed = {
  getFeed: (eventId, limit = 50) => api.get(`/events/${eventId}/feed`, { params: { limit } }),
  getPost: (eventId, postId) => api.get(`/events/${eventId}/feed/${postId}`),
  like: (eventId, postId) => api.post(`/events/${eventId}/feed/${postId}/like`),
  addComment: (eventId, postId, data) => api.post(`/events/${eventId}/feed/${postId}/comment`, data),
};

// ==================== CLUBS ====================

export const clubs = {
  create: (data) => api.post('/clubs', data),
  getAll: (params) => api.get('/clubs', { params }),
  getById: (clubId) => api.get(`/clubs/${clubId}`),
  search: (params) => api.get('/clubs/search', { params }),
  join: (clubId) => api.post(`/clubs/${clubId}/join`),
  leave: (clubId) => api.post(`/clubs/${clubId}/leave`),
  getLeaderboard: (clubId, category = 'trainings') =>
    api.get(`/clubs/${clubId}/leaderboard`, { params: { category } }),
};

// ==================== COACH ====================

export const coach = {
  createPlan: (data) => api.post('/coach/plans', data),
  getPlans: (params) => api.get('/coach/plans', { params }),
  createSession: (data) => api.post('/coach/sessions', data),
  getSessions: () => api.get('/coach/sessions'),
  enrollSession: (sessionId) => api.post(`/coach/sessions/${sessionId}/enroll`),
  getAthletes: () => api.get('/coach/athletes'),
  getAnalytics: () => api.get('/coach/analytics'),
};

// ==================== HEAD COACH ====================

export const headCoach = {
  getStrategy: () => api.post('/head-coach/strategy'),
};

// ==================== COACH DASHBOARD ====================

export const coachDashboard = {
  getAthletes: () => api.get('/coach/dashboard/athletes'),
  getAthleteProgress: (athleteId) => api.get(`/coach/dashboard/athletes/${athleteId}/progress`),
  getAthleteRecommendations: (athleteId) => api.get(`/coach/dashboard/athletes/${athleteId}/recommendations`),
  getSchedule: () => api.get('/coach/dashboard/schedule'),
};

// ==================== SUBSCRIPTION ====================

export const subscription = {
  get: () => api.get('/subscription'),
  getPlans: () => api.get('/subscription/plans'),
  createCheckout: (planId, idempotencyKey) =>
    api.post('/subscription/checkout', {
      plan_id: planId,
      idempotency_key:
        idempotencyKey ||
        (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${planId}`),
    }),
  cancel: (payload = {}) =>
    api.post('/subscription/cancel', {
      immediate: false,
      reason_code: 'user_request',
      reason_text: 'Requested cancellation from subscription settings',
      ...payload,
    }),
  applyPromoCode: (code) => api.post('/subscription/promo-code', null, { params: { code } }),
  usePromoCode: (code) => api.post('/subscription/promo-code/use', null, { params: { code } }),
};

// ==================== LEADERBOARD ====================

export const leaderboard = {
  get: () => outOfScopeV10('leaderboard.get'),
  update: () => outOfScopeV10('leaderboard.update'),
};

// ==================== CALENDAR ====================

export const calendar = {
  getStatus: () => api.get('/calendar/status'),
  getAuthUrl: (fallback = false) => api.get('/calendar/auth-url', { params: { fallback } }),
  integrate: (data) => api.post('/calendar/integrate', data),
  getIntegrations: () => api.get('/calendar/integrations'),
  disconnect: (integrationId) => api.delete(`/calendar/integration/${integrationId}`),
};

// ==================== EXPORT ====================

export const exportData = {
  trainingsCSV: (params) => api.get('/export/trainings.csv', { params, responseType: 'blob' }),
  statisticsCSV: (params) => api.get('/export/statistics.csv', { params, responseType: 'blob' }),
  progressPDF: (params) => api.get('/export/progress.pdf', { params, responseType: 'blob' }),
  calendarICS: (params) => api.get('/export/calendar.ics', { params, responseType: 'blob' }),
};

// ==================== AI COACH ====================

export const aiCoach = {
  getStatus: () => api.get('/ai/status'),
  getAnalysis: (fallback = false) => api.get('/ai/analysis', { params: { fallback } }),
  getWorkoutRecommendation: (fallback = false) =>
    api.get('/ai/workout-recommendation', { params: { fallback } }),
};

// ==================== AI EXTRAS (v1.0.1 lock) ====================

export const AI_EXTRAS_CONTRACT_VERSION = 'v1.0.1-lock';
export const AI_EXTRAS_ALLOWED = Object.freeze(['quick_tips', 'session_brief']);

const toTrimmedString = (value) => String(value ?? '').trim();

const normalizeInteger = (value, field) => {
  const normalized = Number(value);
  if (!Number.isInteger(normalized)) {
    throw new Error(`${field} must be an integer`);
  }
  return normalized;
};

export const validateQuickTipsPayload = (payload = {}) => {
  const context = toTrimmedString(payload.context);
  const daysPerWeek = normalizeInteger(payload.days_per_week, 'days_per_week');

  if (context.length === 0 || context.length > 320) {
    throw new Error('context length must be between 1 and 320 characters');
  }
  if (daysPerWeek < 1 || daysPerWeek > 7) {
    throw new Error('days_per_week must be between 1 and 7');
  }

  return {
    context,
    days_per_week: daysPerWeek,
  };
};

export const validateSessionBriefPayload = (payload = {}) => {
  const durationMin = normalizeInteger(payload.duration_min, 'duration_min');
  const keyGoal = toTrimmedString(payload.key_goal);
  const constraints = Array.isArray(payload.constraints)
    ? payload.constraints.map((item) => toTrimmedString(item)).filter(Boolean)
    : [];

  if (durationMin < 30 || durationMin > 240) {
    throw new Error('duration_min must be between 30 and 240');
  }
  if (keyGoal.length === 0 || keyGoal.length > 140) {
    throw new Error('key_goal length must be between 1 and 140 characters');
  }
  if (constraints.length > 4) {
    throw new Error('constraints must have at most 4 items');
  }

  return {
    duration_min: durationMin,
    key_goal: keyGoal,
    constraints,
  };
};

export const isAiExtraEnabled = (contract, extraKey) => {
  if (!contract || contract.contract_version !== AI_EXTRAS_CONTRACT_VERSION) {
    return false;
  }
  const allowedExtras = Array.isArray(contract.allowed_extras) ? contract.allowed_extras : [];
  return allowedExtras.some((item) => {
    if (typeof item === 'string') {
      return item === extraKey;
    }
    return item?.key === extraKey;
  });
};

export const resolveEnabledAiExtras = (contract) => ({
  quickTips: isAiExtraEnabled(contract, 'quick_tips'),
  sessionBrief: isAiExtraEnabled(contract, 'session_brief'),
});

export const aiExtras = {
  getContract: () => api.get('/ai/extras/contract'),
  getQuickTips: (payload) => api.post('/ai/extras/quick-tips', validateQuickTipsPayload(payload)),
  getSessionBrief: (payload) => api.post('/ai/extras/session-brief', validateSessionBriefPayload(payload)),
};

// ==================== AI TRAINING PLANNER ====================

export const trainingPlanner = {
  generate: (data) => api.post('/training/planner', data),
  getHistory: (limit = 10) => api.get('/training/planner/history', { params: { limit } }),
  analyzeGoals: (data) => api.post('/training/planner/analyze-goals', data),
  getRecovery: () => api.get('/training/planner/recovery'),
  getProgress: (limit = 5) => api.get('/training/planner/progress', { params: { limit } }),
  comparePlans: (planAId, planBId) => api.post('/training/planner/compare', { plan_a_id: planAId, plan_b_id: planBId }),
  exportPdf: (planId) => api.get(`/training/planner/${planId}/pdf`, { responseType: 'blob' }),
};

// ==================== ADMIN ====================

export const admin = {
  getPayments: () => api.get('/admin/payments'),
  getAnalytics: () => api.get('/admin/analytics'),
  getCostUsage: () => api.get('/admin/cost-usage'),
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUserRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, { role }),
};

// ==================== HEALTH ====================

export const health = {
  check: () => api.get('/health'),
  ready: () => api.get('/ready'),
};

// ==================== WEBSOCKET ====================

export const createWebSocket = async () => {
  return openAuthenticatedWebSocket({
    tokenProvider: async () => {
      const { data } = await auth.getWsToken();
      return data?.token;
    },
  });
};

export default api;
